import { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { apiCallErrorHandler } from '../utils/common';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Fields to skip in the comparison table (internal/meta)
const SKIP_FIELDS = new Set(['ConfigVersionNo', 'SessionId', 'IsReviewed', 'CreatedAt', 'CreatedBy', 'VersionType', 'Equipments info']);

// Equipment fields (the 24 simplified equipment columns)
const EQUIPMENT_FIELDS = new Set([
  'PanoramicRoof', 'GPS', 'MirrorLink', 'Rims', 'AirConditioning', 'LED',
  'Radar', 'Camera', 'SpeedControl', 'SunRoof', 'DarkTintedWindows',
  'HeatedSeats', 'LeatherSteeringWheel', 'RoofBars', 'LeatherSeats',
  'FrontBenchSeat', 'Partition', 'Bluetooth', 'PowerWindows', 'SlidingDoor',
  'CruiseControl', 'ParkingSensors', 'WoodenFloor', 'AccessRamp',
]);

function formatVal(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function isDiff(a, b) {
  return formatVal(a).toLowerCase() !== formatVal(b).toLowerCase();
}

function EquipmentCell({ value }) {
  const v = formatVal(value);
  if (!v) return <span style={{ color: '#999' }}>—</span>;
  const lower = v.toLowerCase();
  if (lower === 'yes' || lower === '✓' || lower.startsWith('✓'))
    return <Tag value={v} severity="success" style={{ fontSize: '0.75rem' }} />;
  if (lower === 'no' || lower === '✗' || lower.startsWith('✗'))
    return <Tag value={v} severity="danger" style={{ fontSize: '0.75rem' }} />;
  if (lower === 'probably' || lower === '?' || lower.startsWith('?'))
    return <Tag value={v} severity="warning" style={{ fontSize: '0.75rem' }} />;
  return <span>{v}</span>;
}

function VinComparisonPanel({ original, reviewed, sources, allSourceTypes, showSources, allKeys }) {
  const rows = allKeys
    .filter((k) => !SKIP_FIELDS.has(k))
    .map((k) => ({
      field: k,
      original: original?.[k] ?? null,
      reviewed: reviewed?.[k] ?? null,
      changed: isDiff(original?.[k], reviewed?.[k]),
      isEquipment: EQUIPMENT_FIELDS.has(k),
    }));

  const changedOnly = rows.filter((r) => r.changed);
  const [showAll, setShowAll] = useState(false);
  const displayed = showAll ? rows : changedOnly;

  const scrollable = showSources && allSourceTypes.length > 0;

  // Fixed min-widths when scrollable; percentage-based when not
  const baseThStyle = { padding: '4px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', resize: 'horizontal', overflow: 'hidden', minWidth: '40px' };
  const fieldColStyle = scrollable
    ? { ...baseThStyle, minWidth: 160, whiteSpace: 'nowrap' }
    : { ...baseThStyle, width: '22%' };
  const dataColStyle = scrollable
    ? { ...baseThStyle, minWidth: 200 }
    : { ...baseThStyle, width: '39%' };
  const srcColStyle = { ...baseThStyle, minWidth: 200 };

  const fieldCellStyle = (changed) => ({
    padding: '3px 8px',
    fontWeight: changed ? 600 : 400,
    color: changed ? '#b45309' : '#333',
    whiteSpace: 'nowrap',
  });
  const dataCellStyle = (color) => scrollable
    ? { padding: '3px 8px', color, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
    : { padding: '3px 8px', color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 };

  return (
    <div style={{ padding: '0.5rem 1rem' }}>
      <div style={{ marginBottom: '0.5rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <span style={{ fontSize: '0.8rem', color: '#666' }}>
          {changedOnly.length} field{changedOnly.length !== 1 ? 's' : ''} changed
        </span>
        <Button
          label={showAll ? 'Show changed only' : 'Show all fields'}
          className="p-button-text p-button-sm"
          style={{ fontSize: '0.75rem', padding: '0.1rem 0.4rem' }}
          onClick={() => setShowAll((p) => !p)}
        />
      </div>
      <div style={scrollable ? { overflowX: 'auto', width: '100%' } : {}}>
      <table style={{ width: scrollable ? 'max-content' : '100%', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: scrollable ? 'auto' : 'fixed' }}>
        <thead>
          <tr style={{ background: '#f4f4f4' }}>
            <th style={fieldColStyle}>Field</th>
            <th style={dataColStyle}>Configured</th>
            <th style={dataColStyle}>Reviewed{reviewed?.CreatedBy ? ` by ${reviewed.CreatedBy}` : ''}</th>
            {showSources && allSourceTypes.map((src) => (
              <th key={src} style={srcColStyle}>{src}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayed.length === 0 && (
            <tr>
              <td colSpan={2 + (showSources ? allSourceTypes.length : 0) + 1} style={{ padding: '8px', color: '#888', fontStyle: 'italic' }}>
                No differences found
              </td>
            </tr>
          )}
          {displayed.map((r) => (
            <tr
              key={r.field}
              style={{
                background: r.changed ? '#fff8e1' : 'transparent',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <td style={fieldCellStyle(r.changed)}>
                {r.field}
              </td>
              <td title={formatVal(r.original)} style={dataCellStyle(r.changed ? '#c0392b' : '#333')}>
                {r.isEquipment ? <EquipmentCell value={r.original} /> : <span>{formatVal(r.original)}</span>}
              </td>
              <td title={formatVal(r.reviewed)} style={dataCellStyle(r.changed ? '#27ae60' : '#333')}>
                {r.isEquipment ? <EquipmentCell value={r.reviewed} /> : <span>{formatVal(r.reviewed)}</span>}
              </td>
              {showSources && allSourceTypes.map((src) => {
                const srcVal = sources?.[src]?.[r.field] ?? null;
                return (
                  <td key={src} title={formatVal(srcVal)} style={dataCellStyle('#555')}>
                    {r.isEquipment ? <EquipmentCell value={srcVal} /> : <span>{formatVal(srcVal)}</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default function ReviewComparisonPage() {
  const { reviewSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const batchTitle = searchParams.get('batchTitle') || null;
  const { getAccessTokenSilently } = useAuth0();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [expandedRows, setExpandedRows] = useState(null);
  const [showSources, setShowSources] = useState(false);

  useEffect(() => { document.title = 'OptiConfig Review Comparison'; }, []);

  useEffect(() => {
    if (!reviewSessionId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`${API_BASE_URL}/session/compare/${reviewSessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) await apiCallErrorHandler(res, 'Failed to load comparison data');
        const data = await res.json();
        setRows(data.rows ?? data.Rows ?? data ?? []);
      } catch (err) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message, life: 4000 });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAccessTokenSilently, reviewSessionId]);

  // Group rows by VIN: { vin -> { original: row|null, reviewed: row|null, sources: {[versionType]: row} } }
  const { vinRows, allKeys, allSourceTypes } = useMemo(() => {
    const map = {};
    const keySet = new Set();
    const sourceTypeSet = new Set();
    rows.forEach((r) => {
      const vin = r['VIN'] ?? r['Vin'] ?? r['vin'] ?? '';
      Object.keys(r).forEach((k) => keySet.add(k));
      if (!map[vin]) map[vin] = { vin, original: null, reviewed: null, sources: {}, changedCount: 0 };
      const versionType = r['VersionType'] ?? '';
      if (versionType === 'Reviewed') {
        map[vin].reviewed = r;
      } else if (versionType === 'Configured') {
        map[vin].original = r;
      } else {
        map[vin].sources[versionType] = r;
        if (versionType) sourceTypeSet.add(versionType);
      }
    });

    const allKeys = [...keySet];
    const allSourceTypes = [...sourceTypeSet].sort();

    // Compute changed field counts (original vs reviewed only)
    Object.values(map).forEach((entry) => {
      const { original, reviewed } = entry;
      if (!original || !reviewed) { entry.changedCount = 0; return; }
      entry.changedCount = allKeys.filter(
        (k) => !SKIP_FIELDS.has(k) && isDiff(original[k], reviewed[k])
      ).length;
    });

    return { vinRows: Object.values(map), allKeys, allSourceTypes };
  }, [rows]);

  const rowExpansionTemplate = (row) => (
    <VinComparisonPanel
      original={row.original}
      reviewed={row.reviewed}
      sources={row.sources}
      allSourceTypes={allSourceTypes}
      showSources={showSources}
      allKeys={allKeys}
    />
  );

  const changedBadge = (row) => {
    if (row.original == null || row.reviewed == null) return null;
    if (row.changedCount === 0) return <Tag value="No changes" severity="success" style={{ fontSize: '0.7rem' }} />;
    return <Tag value={`${row.changedCount} changed`} severity="warning" style={{ fontSize: '0.7rem' }} />;
  };

  const hasOriginal = (row) => row.original != null;
  const hasReviewed = (row) => row.reviewed != null;

  const handleExport = async () => {
    if (!reviewSessionId) return;
    setExporting(true);
    try {
      const token = await getAccessTokenSilently();
      const params = batchTitle ? `?batchTitle=${encodeURIComponent(batchTitle)}` : '';
      const res = await fetch(`${API_BASE_URL}/session/compare/${reviewSessionId}/export${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) await apiCallErrorHandler(res, 'Failed to export comparison data');
      const blob = await res.blob();
      const safeBatchTitle = batchTitle ? batchTitle.trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '') : '';
      const fileName = `${safeBatchTitle || reviewSessionId}_review_comp.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message, life: 4000 });
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4rem' }}>
        <ProgressSpinner />
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem 2rem', maxWidth: 1400, margin: '0 auto' }}>
      <Toast ref={toast} />
      <h3 style={{ textAlign: 'center', marginBottom: '0.25rem' }}>
        OptiConfig Review Comparison
        {batchTitle && (
          <span style={{ fontWeight: 400, opacity: 0.75, marginLeft: '0.5rem' }}>— {batchTitle}</span>
        )}
      </h3>
      <p style={{ textAlign: 'center', color: '#666', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
        {vinRows.length} Sample{vinRows.length !== 1 ? 's' : ''}
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {allSourceTypes.length > 0 && (
          <Button
            label={showSources ? 'Hide sources' : `Show all sources`}
            icon={showSources ? 'pi pi-eye-slash' : 'pi pi-eye'}
            className="p-button-sm p-button-outlined"
            onClick={() => setShowSources((p) => !p)}
          />
        )}
        <Button
          label="Export to Excel"
          icon="pi pi-file-excel"
          className="p-button-sm p-button-outlined p-button-success"
          loading={exporting}
          onClick={handleExport}
        />
      </div>

      <DataTable
        value={vinRows}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        dataKey="vin"
        style={{ fontSize: '0.8rem' }}
        tableStyle={{ tableLayout: 'fixed' }}
        size="small"
        sortField="vin"
        sortOrder={1}
      >
        <Column expander style={{ width: '3rem' }} />
        <Column field="vin" header="VIN" sortable style={{ fontFamily: 'monospace', fontWeight: 600, width: '12rem' }} />
        <Column
          header="Make / Model"
          body={(row) => {
            const r = row.reviewed ?? row.original;
            return r ? `${r['Make'] ?? ''} ${r['Model'] ?? ''} ${r['Submodel'] ?? ''}`.trim() : '';
          }}
        />
        <Column
          header="Version"
          body={(row) => {
            const r = row.reviewed ?? row.original;
            return r?.['Version'] ?? '';
          }}
        />
        <Column
          header=""
          style={{ width: '2.5rem', textAlign: 'center' }}
          body={(row) => {
            const missingOriginal = row.original == null;
            const missingReviewed = row.reviewed == null;
            if (!missingOriginal && !missingReviewed) return null;
            const missing = missingOriginal && missingReviewed
              ? 'Both configured and reviewed versions are missing'
              : missingOriginal
              ? 'Configured version is missing'
              : 'Reviewed version is missing';
            const tipId = `tip-missing-${row.vin}`;
            return (
              <>
                <Tooltip target={`#${tipId}`} content={missing} position="top" />
                <span
                  id={tipId}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '1.25rem',
                    height: '1.25rem',
                    borderRadius: '50%',
                    background: '#e53935',
                    color: '#fff',
                    fontWeight: 900,
                    fontSize: '0.75rem',
                    cursor: 'default',
                    userSelect: 'none',
                  }}
                >
                  !
                </span>
              </>
            );
          }}
        />
        {/* <Column header="Original" body={(row) => hasOriginal(row)
          ? <Tag value="✓" severity="info" style={{ fontSize: '0.7rem' }} />
          : <Tag value="—" severity="secondary" style={{ fontSize: '0.7rem' }} />
        } style={{ width: '7rem' }} />
        <Column header="Reviewed" body={(row) => hasReviewed(row)
          ? <Tag value="✓" severity="success" style={{ fontSize: '0.7rem' }} />
          : <Tag value="—" severity="secondary" style={{ fontSize: '0.7rem' }} />
        } style={{ width: '7rem' }} /> */}
        <Column header="Diff" body={changedBadge} style={{ width: '8rem' }} />
      </DataTable>
    </div>
  );
}
